import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Flame } from "lucide-react";
import { BURN_TX, SHORTEN } from "../config";
import usePoll, { fmtNum, timeAgo } from "../hooks/usePoll";
import { EASE } from "./Reveal";

const FIRST_BURN_SIG = BURN_TX.replace("https://solscan.io/tx/", "");

const fmtDate = (t) =>
    t ? new Date(t * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

function BurnRow({ b, i }) {
    return (
        <motion.li
            layout
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
            data-testid={`burn-row-${b.signature.slice(0, 8)}`}
            className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-xl border border-white/5 bg-black/30 px-4 py-3 text-left sm:grid-cols-[auto_1fr_auto_auto]"
        >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/15 text-orange-400">
                <Flame className="h-4 w-4" />
            </span>
            <div className="min-w-0">
                <div className="font-mono text-sm font-bold text-orange-200">
                    {fmtNum(b.amount)} <span className="text-orange-400/70">$BALD</span>
                </div>
                <div className="truncate font-mono text-[11px] text-slate-500">{SHORTEN(b.signature, 8, 8)}</div>
            </div>
            <span className="hidden font-mono text-[11px] uppercase text-slate-500 sm:block">{fmtDate(b.blockTime)}</span>
            <a
                href={b.solscan}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`burn-row-link-${b.signature.slice(0, 8)}`}
                className="flex items-center gap-1 font-mono text-[11px] font-bold uppercase tracking-widest text-cyan-300 hover:text-cyan-100"
            >
                SOLSCAN <ExternalLink className="h-3 w-3" />
            </a>
        </motion.li>
    );
}

export default function BurnTracker() {
    const { data, loading } = usePoll("/burns", 120000);
    const burns = data?.burns?.length
        ? data.burns
        : loading
          ? []
          : [{ signature: FIRST_BURN_SIG, amount: null, blockTime: null, solscan: BURN_TX }];
    const total = data?.totalBurned;

    return (
        <div
            data-testid="burn-verification-card"
            className="glass mx-auto mt-14 flex max-w-3xl flex-col items-center gap-6 rounded-2xl border-orange-500/25 p-6 text-center md:p-8"
        >
            <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-orange-400">
                <Flame className="h-4 w-4" /> VERIFIED BURN TRANSACTIONS
            </div>

            <div className="grid w-full grid-cols-2 gap-3">
                <div className="rounded-xl border border-orange-500/20 bg-orange-500/5 p-4">
                    <div className="kicker !text-[10px] !text-orange-400/80">TOTAL BURNED</div>
                    <div data-testid="burn-total" className="headline mt-1 text-2xl text-orange-300 md:text-3xl">
                        {total != null ? fmtNum(total) : "—"}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">$BALD · ON-CHAIN</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                    <div className="kicker !text-[10px]">BURN EVENTS</div>
                    <div data-testid="burn-count" className="headline mt-1 text-2xl text-white md:text-3xl">
                        {data?.count != null ? data.count : "—"}
                    </div>
                    <div className="font-mono text-[10px] text-slate-500">
                        {data?.updatedAt ? `SYNCED ${timeAgo(data.updatedAt).toUpperCase()}` : "AUTO-SYNCING"}
                    </div>
                </div>
            </div>

            <p className="text-sm text-slate-400">
                Burns are detected automatically from the dev wallet on Solana. Every entry is an on-chain transaction anyone can verify.
            </p>

            <ul data-testid="burn-list" className="flex w-full flex-col gap-2">
                <AnimatePresence initial={false}>
                    {burns.map((b, i) => (
                        <BurnRow key={b.signature} b={b} i={i} />
                    ))}
                </AnimatePresence>
                {loading && (
                    <li data-testid="burn-list-loading" className="animate-pulse rounded-xl border border-white/5 bg-black/30 px-4 py-5 font-mono text-[11px] uppercase tracking-widest text-slate-500">
                        SCANNING SOLANA FOR BURNS…
                    </li>
                )}
            </ul>

            <a
                href={`https://solscan.io/account/${data?.wallet || "HdmQS1vKbeWFLLwaM3pzWMQE7H8NvawrNSSntbcGMf7g"}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="burn-verify-solscan-button"
                className="btn-primary !bg-orange-500 !text-black shadow-[0_0_24px_rgba(255,61,0,0.35)] hover:shadow-[0_0_44px_rgba(255,61,0,0.55)]"
            >
                VERIFY BURN WALLET ON SOLSCAN <ExternalLink className="h-4 w-4" />
            </a>
        </div>
    );
}
